<template>
  <div>
    <div class="center-content w-full">
      <Card class="w-full">
        <template v-slot:content>
          <h1 class="page-title">Upload & Import Notifica Applicazione Indennizzo</h1>
          <div class="flex align-items-center flex-column">
            <div class="button-wrapper mb-2">
              <span class="label">{{
                filesToUpload.length == 0
                  ? "Seleziona File"
                  : filesToUpload.length + " file(s) selezionati"
              }}</span>
              <input id="uploadFile" type="file" ref="fileInput" @change="handleFileUpload($event)"
                accept=".pdf,.xlsx,.xls,.csv" multiple />
            </div>
            <div v-show="validationErrors.files && submitted" class="p-error">
              Selezionare almeno un file.
            </div>
            <div v-show="validationErrors.type && submitted" class="p-error">
              Formato non consentito (solo .pdf, .xlsx, .xls, .csv)
            </div>
            <div class="text-center mt-3">
              <small>Formati consentiti: .pdf, .xlsx, .xls, .csv</small>
            </div>
            <div class="button-group">
              <Button @click="uploadFiles" label="Carica File" class="upload-button action-button" :disabled="loading" />
              <Button @click="importFiles" label="Importa File" class="import-button action-button" :disabled="loadingImport" />
            </div>
            <div v-if="loading || loadingImport" class="mt-3">
              <ProgressSpinner style="width:50px;height:50px" strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s"/>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { ref, getCurrentInstance } from "vue";
import Service from "@/services/ask-caricamento-file/caricamento-file";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import Card from "primevue/card";
import ProgressSpinner from 'primevue/progressspinner';

export default {
  components: {
    Button,
    Card,
    ProgressSpinner
  },
  setup() {
    const toast = useToast();
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const filesToUpload = ref([]);
    const validationErrors = ref({});
    const submitted = ref(false);
    const loading = ref(false);
    const loadingImport = ref(false);

    const handleFileUpload = (evt) => {
      submitted.value = false;
      filesToUpload.value = self.refs.fileInput.files;
      validationErrors.value = {};
    };

    const validateForm = () => {
      validationErrors.value = {};
      if (filesToUpload.value.length === 0) {
        validationErrors.value['files'] = true;
      } else {
        const allowedExtensions = ['pdf', 'xlsx', 'xls', 'csv'];
        let valid = [...filesToUpload.value].every(file => {
          const extension = file.name.split('.').pop()?.toLowerCase();
          return extension && allowedExtensions.includes(extension);
        });
        if (!valid) validationErrors.value['type'] = true;
      }
      return Object.keys(validationErrors.value).length === 0;
    };

    const uploadFiles = async () => {
      submitted.value = true;
      if (validateForm()) {
        loading.value = true;
        try {
          const formData = new FormData();
          for (let i = 0; i < filesToUpload.value.length; i++) {
            formData.append('file', filesToUpload.value[i]);
          }

          const response = await service.uploadNotificaIndennizzo(formData);

          if (response.success) {
            toast.add({
              severity: 'success',
              summary: 'Caricamento Completato',
              detail: response.message,
              life: 5000
            });

            filesToUpload.value = [];
            if (self.refs.fileInput) {
              self.refs.fileInput.value = null;
            }
            submitted.value = false;
          } else {
            toast.add({
              severity: 'error',
              summary: 'Errore',
              detail: response.message,
              life: 5000
            });
          }
        } catch (error) {
          console.error("Errore durante il caricamento dei file:", error);
          toast.add({
            severity: "error",
            summary: "Errore Caricamento",
            detail: error.response?.data?.message || error.message || "Si è verificato un errore durante il caricamento.",
            life: 5000,
          });
        } finally {
          loading.value = false;
        }
      }
    };

    const importFiles = async () => {
      loadingImport.value = true;
      try {
        const response = await service.importNotificaIndennizzo();

        // Controllo se non ci sono file importati
        const noFiles =
          !response.data ||
          !response.data.imported_files ||
          response.data.imported_files.length === 0;

        if (response.success && noFiles) {
          toast.add({
            severity: 'info',
            summary: 'Nessun File da Importare',
            detail: 'Non ci sono file da importare.',
            life: 5000
          });
        } else if (response.success) {
          let detailMessage = response.message;
          if (response.data) {
            if (response.data.imported_files && response.data.imported_files.length > 0) {
              detailMessage += `\nFile importati: ${response.data.imported_files.join(', ')}`;
            }
            if (response.data.errors && response.data.errors.length > 0) {
              detailMessage += `\nErrori riscontrati: ${response.data.errors.map(e => `${e.file}: ${e.error}`).join(', ')}`;
            }
          }
          toast.add({
            severity: 'success',
            summary: 'Importazione Completata',
            detail: detailMessage,
            life: 7000
          });
        } else {
          toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: response.message,
            life: 5000
          });
        }
      } catch (error) {
        console.error("Errore durante l'importazione dei file:", error);
        toast.add({
          severity: "error",
          summary: "Errore Importazione",
          detail: error.response?.data?.message || error.message || "Si è verificato un errore durante l'importazione.",
          life: 5000,
        });
      } finally {
        loadingImport.value = false;
      }
    };

    return {
      filesToUpload,
      validationErrors,
      submitted,
      handleFileUpload,
      uploadFiles,
      importFiles,
      loading,
      loadingImport
    };
  },
};
</script>

<style scoped>
:root {
  --primary-color: #1f5d38;
  --secondary-color: #164628;
  --accent-color: #63796c;
  --background-color: #f8fafc;
  --text-primary: #1a1a1a;
  --text-secondary: #666666;
  --border-color: #e5e7eb;
  --success-color: #4CAF50;
  --error-color: #dc2626;
  --card-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.center-content {
  width: 100%;
  padding: 1rem;
}

.w-full {
  width: 100%;
}

.page-title {
  text-align: center;
  margin: 10px auto 20px auto;
  font-size: 1.25rem;
  color: var(--text-primary);
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--border-color);
}

.p-error {
  color: var(--error-color);
  margin-top: 0.5rem;
  text-align: center;
  width: 70%;
  margin-left: auto;
  margin-right: auto;
}

.upload-button {
  margin-top: 1rem;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  padding: 0.75rem 1.5rem;
}

.upload-button:hover {
  background-color: var(--secondary-color) !important;
  border-color: var(--secondary-color) !important;
}

.import-button {
  margin-top: 1rem;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  padding: 0.75rem 1.5rem;
}

.import-button:hover {
  background-color: var(--secondary-color) !important;
  border-color: var(--secondary-color) !important;
}

.button-wrapper {
  position: relative;
  width: 250px;
  text-align: center;
  margin-top: 1rem;
}

.button-wrapper span.label {
  position: relative;
  z-index: 0;
  display: inline-block;
  width: 100%;
  background: #1f5d38;
  cursor: pointer;
  color: #fff;
  padding: 10px 0;
  text-transform: uppercase;
  font-size: 12px;
  border-radius: 3px;
  box-shadow: 0px 10px 13px -7px #00000050, 5px 5px 15px 5px rgba(0, 0, 0, 0);
}

#uploadFile {
  display: inline-block;
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 50px;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
  font-size: 0;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.mt-3 {
  margin-top: 1.5rem;
}

.action-button {
  min-width: 200px;
  height: 56px;
  font-weight: bold;
  font-size: 1.1rem;
  justify-content: center;
  display: flex;
  align-items: center;
  text-align: center;
  padding: 0;
  box-sizing: border-box;
}
</style>
